import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography, CircularProgress,
} from '@mui/material';
import { GoodsReceiptService } from '../../services/goodsReceipt.service';
import { PurchaseOrderService } from '../../services/purchaseOrder.service';
import { ItemService } from '../../services/item.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import { SegmentService } from '../../services/segment.service';
import { AuthService } from '../../services/auth.service';
import { PurchaseOrder, PurchaseOrderLine } from '../../interfaces/procurement/purchaseOrder.interface';
import { Item } from '../../interfaces/item.interface';
import { Store } from '../../interfaces/store.interface';
import { Segment } from '../../interfaces/accounting.interface';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import FabricAttributeFields, { FabricAttributes } from '../common/FabricAttributeFields';
import { FabricGrade } from '../../interfaces/stock.interface';
import SearchableSelect from '../common/SearchableSelect';
import LotDateFields, { LotDates, lotDatesError, lotDatesPayload } from '../common/LotDateFields';
import { toLocalIsoDate } from '../../utils/dateUtils';
import ItemScanField, { ScanOutcome } from '../item/ItemScanField';   // Q3

const outstandingOf = (l: PurchaseOrderLine): number => Math.max(0, l.quantity - (l.receivedQty ?? 0));
const toDraft = (l: PurchaseOrderLine): LineDraft => ({ poLine: l, receivedQty: outstandingOf(l) });

interface LineDraft {
  poLine: PurchaseOrderLine;
  receivedQty: number;
  segmentId?: string;
  dyeLot?: string;
  shade?: string;
  widthCm?: number;
  gsm?: number;
  rollCount?: number;
  supplierBatchRef?: string;
  grade?: FabricGrade;
  notes?: string;
  expiryDate?: string;       // F6a
  manufactureDate?: string;  // F6a
}

export const GoodsReceiptCreate: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const lockedPoId = searchParams.get('purchaseOrderId');

  const [pos, setPos] = useState<PurchaseOrder[]>([]);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [storeId, setStoreId] = useState('');
  const [receiptDate, setReceiptDate] = useState(toLocalIsoDate());
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [linesLoading, setLinesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const scannedLines = useRef<Set<string>>(new Set());   // Q3: PO lines counted by scanning; reset whenever the PO's lines reload

  const itemMap = useMemo(() => new Map(items.map(i => [i.id, i])), [items]);

  useEffect(() => {
    const init = async () => {
      try {
        const [itemsRes, storesRes] = await Promise.all([
          ItemService.getItemsByInstitution(0, 500, '', 'STOCK'), // FW-2A
          getStoresByInstitutionId(0, 500),
        ]);
        setItems(itemsRes.content);
        setStores(storesRes.content);
        // Segments are an optional per-line annotation — never let their fetch block GRN creation.
        SegmentService.getByInstitution(0, 500)
          .then(res => setSegments((res.content as Segment[]).filter(s => s.active !== false)))
          .catch(() => setSegments([]));

        if (lockedPoId) {
          const po = await PurchaseOrderService.getById(lockedPoId);
          setSelectedPO(po);
          scannedLines.current.clear();
          setLines((po.lines ?? []).map(toDraft));
        } else {
          const [orderedRes, partialRes] = await Promise.all([
            PurchaseOrderService.listByInstitution(0, 500, 'ORDERED'),
            PurchaseOrderService.listByInstitution(0, 500, 'PARTIALLY_RECEIVED'),
          ]);
          setPos([...orderedRes.content, ...partialRes.content]);
        }
      } catch (e) {
        toast.showApiError(e, 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePOChange = async (poId: string) => {
    scannedLines.current.clear();
    if (!poId) { setSelectedPO(null); setLines([]); return; }
    // The list endpoint omits nested lines (returns lines: null), so fetch the
    // full PO detail to populate the receivable lines.
    setLinesLoading(true);
    setLines([]);
    try {
      const po = await PurchaseOrderService.getById(poId);
      setSelectedPO(po);
      setLines((po.lines ?? []).map(toDraft));
    } catch (e) {
      toast.showApiError(e, 'Failed to load purchase order lines');
    } finally {
      setLinesLoading(false);
    }
  };

  // ---- Q3: scan-to-add. The first scan of a line sets its received qty to 1, each further scan adds 1, up to the outstanding qty. ----
  const linesRef = useRef<LineDraft[]>([]);
  linesRef.current = lines;
  const handleScan = (item: Item): ScanOutcome => {
    const current = linesRef.current;
    const idx = current.findIndex(l => l.poLine.itemId === item.id);
    const label = `${item.itemCode} - ${item.itemName}`;
    if (idx < 0) return { ok: false, message: `${label} is not on this purchase order.` };
    const line = current[idx];
    const max = outstandingOf(line.poLine);
    if (max <= 0) return { ok: false, message: `${label} is already fully received on this purchase order.` };
    const first = !scannedLines.current.has(line.poLine.id);
    if (!first && line.receivedQty + 1 > max) return { ok: false, message: `${label} is already at the outstanding quantity (${max}).` };
    const startingScan = scannedLines.current.size === 0;
    const qty = first ? Math.min(max, 1) : line.receivedQty + 1;
    scannedLines.current.add(line.poLine.id);
    // Scan mode: the lines start at their full outstanding quantity, so the first scan zeroes the lines that have not been scanned.
    const next = current.map((l, i) => i === idx ? { ...l, receivedQty: qty }
      : startingScan ? { ...l, receivedQty: 0 } : l);
    linesRef.current = next;   // a queued scan may run before React re-renders
    setLines(next);
    return { ok: true, message: `${label}: received quantity is now ${qty} of ${max}.`
      + (startingScan && current.length > 1 ? ' Scan mode: every other line is set to 0 until you scan it.' : '') };
  };

  const updateReceivedQty = (idx: number, qty: number) => {
    setLines(ls => ls.map((l, i) => i === idx ? { ...l, receivedQty: qty } : l));
  };

  const updateSegmentId = (idx: number, segmentId: string) => {
    setLines(ls => ls.map((l, i) => i === idx ? { ...l, segmentId: segmentId || undefined } : l));
  };

  const updateFabricAttributes = (idx: number, next: FabricAttributes) => {
    setLines(ls => ls.map((l, i) => i === idx ? { ...l, ...next } : l));
  };

  // ---- F6a: lot dates per line ----
  const updateLotDates = (idx: number, next: LotDates) => {
    setLines(ls => ls.map((l, i) => i === idx ? { ...l, expiryDate: next.expiryDate, manufactureDate: next.manufactureDate } : l));
  };
  // ---- end F6a ----

  const handleSubmit = async () => {
    if (!selectedPO) { toast.showApiError(null, 'Select a Purchase Order'); return; }
    if (!storeId) { toast.showApiError(null, 'Select a Store'); return; }

    const activeLines = lines.filter(l => l.receivedQty > 0);
    if (activeLines.length === 0) { toast.showApiError(null, 'At least one line must have received quantity > 0'); return; }

    // F6a: an expiry-tracked item's line needs its expiry date; never before the manufacture date
    for (const l of activeLines) {
      const item = itemMap.get(l.poLine.itemId);
      if (!item?.lotTracked) continue;
      const err = lotDatesError(l, !!item.expiryTracked);
      if (err) { toast.showApiError(null, `${item.itemName ?? l.poLine.itemName ?? 'Line'}: ${err}`); return; }
    }

    setSaving(true);
    try {
      const dto = {
        purchaseOrderId: selectedPO.id,
        storeId,
        receiptDate,
        notes: notes || undefined,
        institutionId: AuthService.getSelectedInstitution()!!,
        lines: activeLines.map(l => ({
          purchaseOrderLineId: l.poLine.id,
          receivedQty: l.receivedQty,
          ...(l.segmentId ? { segmentId: l.segmentId } : {}),
          ...(itemMap.get(l.poLine.itemId)?.lotTracked ? {
            dyeLot: l.dyeLot,
            shade: l.shade,
            widthCm: l.widthCm,
            gsm: l.gsm,
            rollCount: l.rollCount,
            supplierBatchRef: l.supplierBatchRef,
            grade: l.grade,
            notes: l.notes,
            ...lotDatesPayload(l),   // F6a
          } : {}),
        })),
      };
      const grn = await GoodsReceiptService.create(dto);
      toast.showSuccess('Goods Receipt created');
      navigate(`/procurement/goods-receipts/${grn.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to create Goods Receipt');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }} data-help-page="store.grnForm.page">
      <Typography variant="h5" sx={{ mb: 3 }}>New Goods Receipt</Typography>

      <Paper sx={{ p: 3, mb: 3 }} data-help-section="store.grnForm.header">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <SearchableSelect<PurchaseOrder>
            options={lockedPoId && selectedPO ? [selectedPO] : pos}
            value={selectedPO?.id ?? ''}
            onChange={id => handlePOChange(id)}
            getOptionLabel={po => po.poNumber}
            data-help="procurement.grnForm.field.po"
            label="Purchase Order"
            size="medium"
            disabled={!!lockedPoId}
          />

          <SearchableSelect<Store>
            options={stores}
            value={storeId}
            onChange={id => setStoreId(id)}
            getOptionLabel={s => s.storeName}
            data-help="procurement.grnForm.field.store"
            label="Store"
            size="medium"
          />

          <TextField
            data-help="procurement.grnForm.field.receiptDate"
            label="Receipt Date"
            type="date"
            value={receiptDate}
            onChange={e => setReceiptDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            data-help="procurement.grnForm.field.notes"
            label="Notes"
            multiline
            rows={2}
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </Box>
      </Paper>

      {linesLoading && (
        <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Paper>
      )}

      {!linesLoading && selectedPO && lines.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }} data-help-section="store.grnForm.lines">
          <Typography variant="h6" sx={{ mb: 2 }}>Lines</Typography>
          <Box data-help="store.grnForm.scan">
          <ItemScanField data-testid="grn-scan" onItem={handleScan}
            label="Scan or type item barcode to count a receipt" />
          </Box>
          <ScrollableTable>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="store.grnForm.col.item">Item</TableCell>
                    <TableCell data-help="store.grnForm.col.ordered">Ordered Qty</TableCell>
                    <TableCell data-help="store.grnForm.col.already">Already Received</TableCell>
                    <TableCell data-help="store.grnForm.col.qty">Received Qty</TableCell>
                    <TableCell data-help="store.grnForm.col.segment">Segment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((l, idx) => (
                    <React.Fragment key={l.poLine.id}>
                    <TableRow>
                      <TableCell>{l.poLine.itemName ?? itemMap.get(l.poLine.itemId)?.itemName ?? '—'}{l.poLine.itemCode ? ` (${l.poLine.itemCode})` : ''}</TableCell>
                      <TableCell>{l.poLine.quantity}{l.poLine.unit ? ` ${l.poLine.unit}` : ''}</TableCell>
                      <TableCell>{l.poLine.receivedQty ?? 0}</TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          data-help="procurement.grnForm.field.lineQty"
                          value={l.receivedQty}
                          onChange={e => updateReceivedQty(idx, Number(e.target.value))}
                          inputProps={{ min: 0, max: outstandingOf(l.poLine) }}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      <TableCell>
                        <SearchableSelect<Segment>
                          options={segments}
                          value={l.segmentId ?? ''}
                          onChange={id => updateSegmentId(idx, id)}
                          getOptionLabel={s => `${s.code} — ${s.name}`}
                          data-help="procurement.grnForm.field.lineSegment"
                          label="Segment"
                          placeholder="(default)"
                          fullWidth={false}
                          sx={{ minWidth: 160 }}
                        />
                      </TableCell>
                    </TableRow>
                    {itemMap.get(l.poLine.itemId)?.lotTracked && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ py: 0 }} data-help-section="procurement.grnForm.section.lotDetails">
                          {/* F6a */}
                          <Box sx={{ pt: 1 }}>
                            <LotDateFields
                              idPrefix={`grn-line-${idx}`}
                              value={l}
                              onChange={next => updateLotDates(idx, next)}
                              expiryRequired={!!itemMap.get(l.poLine.itemId)?.expiryTracked}
                            />
                          </Box>
                          <FabricAttributeFields
                            idPrefix={`grn-line-${idx}`}
                            value={l}
                            onChange={next => updateFabricAttributes(idx, next)}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
          </ScrollableTable>
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button data-help="store.grnForm.create" variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Create GRN'}
        </Button>
        <Button data-help="procurement.grnForm.cancel" variant="outlined" onClick={() => navigate(-1)} disabled={saving}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default GoodsReceiptCreate;
